import { client } from "../api/client.js";
import { escapeHtml } from "../ui/escapeHtml.js";
import { renderPageHeader } from "../ui/pageHeader.js";
import { toast } from '../ui/toast.js';
import { confirmModal } from '../ui/confirmModal.js';
import { openGenerateMonthWizard } from '../ui/generateMonthWizard.js';
import { computeBrainScore } from '../utils/brainScore.js';

const _HOME_ICONS = {
  'rewind': '<polygon points="11 19 2 12 11 5 11 19"/><polygon points="22 19 13 12 22 5 22 19"/>',
  'fast-forward': '<polygon points="13 19 22 12 13 5 13 19"/><polygon points="2 19 11 12 2 5 2 19"/>',
  'skip-back': '<polygon points="19 20 9 12 19 4 19 20"/><line x1="5" y1="19" x2="5" y2="5"/>',
  'skip-forward': '<polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/>',
  'calendar': '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  'send': '<line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>',
  'trash': '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
};
function _homeIcon(name, w = 14, h = 14) {
  return `<svg width="${w}" height="${h}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${_HOME_ICONS[name] || ''}</svg>`;
}

export class HomePage {
  constructor() {
    this.summary = null;
    this.aiHealth = null;
    this.checklist = { mission: false, cta: false, facts: false, generate: false, schedule: false };
    this._eventsBound = false;
    this.loadingIds = new Set();
    this.editingIds = new Set();
    this.editBuffer = {};
    this.selectedItemId = null; // For the details panel
    this.activeMoreMenuId = null; // For the dropdown
    this.isDuplicateMode = false;
    this.isSavingDraft = false;
    this.saveError = null;
    this.onboardingDismissed = localStorage.getItem('cp_onboarding_dismissed') === '1';
    this.warningsDismissed = localStorage.getItem('cp_warnings_dismissed') === '1';

    this._onScrollClose = () => {
      if (this.activeMoreMenuId) {
        this.activeMoreMenuId = null;
        this.render();
      }
    };

    // Stable handler for document level delegation
    this._onDocClick = async (e) => {
      const homeRoot = e.target.closest("#page-home");
      if (!homeRoot) {
        // Handle clicking outside the "More" menu to close it
        if (this.activeMoreMenuId && !e.target.closest('.more-menu') && !e.target.closest('[data-action="open-more"]')) {
          this.activeMoreMenuId = null;
          this.render();
        }
        return;
      }

      // Generate My Month wizard
      if (e.target.closest('#gmw-home-launch')) {
        openGenerateMonthWizard();
        return;
      }

      // Handle "More Menu" toggle
      const moreBtn = e.target.closest('[data-action="open-more"]');
      if (moreBtn) {
        const id = moreBtn.dataset.id;
        this.activeMoreMenuId = this.activeMoreMenuId === id ? null : id;
        this.render();
        return;
      }

      // Close more menu if clicking elsewhere inside Home
      if (this.activeMoreMenuId && !e.target.closest('.more-menu')) {
        this.activeMoreMenuId = null;
        this.render();
      }

      const row = e.target.closest('.schedule-row');
      const actionBtn = e.target.closest('[data-action]');

      if (!actionBtn && row) {
        // Row click = open details
        this.selectedItemId = row.dataset.id;
        this.activeMoreMenuId = null;
        this.isDuplicateMode = false;
        this.saveError = null;
        this.render();
        return;
      }

      if (actionBtn) {
        const action = actionBtn.dataset.action;
        const id = actionBtn.dataset.id || null;
        const item = id ? this.summary.upcoming.find(u => String(u.id) === String(id)) : null;

        if (action === 'dismiss-onboarding') {
          this.onboardingDismissed = true;
          localStorage.setItem('cp_onboarding_dismissed', '1');
          this.render();
          return;
        }

        if (action === 'dismiss-warnings') {
          this.warningsDismissed = true;
          localStorage.setItem('cp_warnings_dismissed', '1');
          this.render();
          return;
        }

        if (action === 'nav-kpi') {
          const target = actionBtn.dataset.target;
          if (target) window.location.hash = target;
          return;
        }

        if (action === 'close-drawer' || action === 'close-menu' || action === 'close-menu-outside') {
          this.selectedItemId = null;
          this.activeMoreMenuId = null;
          this.isDuplicateMode = false;
          this.saveError = null;
          this.render();
          return;
        }

        if (action === 'duplicate' && id) {
          this.selectedItemId = id;
          this.activeMoreMenuId = null;
          this.isDuplicateMode = true;
          this.saveError = null;
          this.render();
        }

        if (action === 'cancel-duplicate') {
          this.isDuplicateMode = false;
          this.saveError = null;
          this.render();
        }

        if (action === 'create-draft' && id) {
          const textarea = homeRoot.querySelector(`textarea[data-id="${id}"]`);
          const content = textarea?.value || item?.contentFull || "";

          this.isSavingDraft = true;
          this.saveError = null;
          this.render();

          try {
            const res = await client.saveToLibrary({
              items: [{
                platform: item.platform,
                content: content,
                status: "draft",
                metadata: { source: "home_duplicate", original_id: id }
              }]
            });

            if (res.success && res.ids && res.ids.length > 0) {
              const newId = res.ids[0];
              sessionStorage.setItem("library:openItemId", newId);
              window.location.hash = "#library";
            } else {
              throw new Error("Failed to create draft");
            }
          } catch (err) {
            console.error("[HOME_DUPLICATE_FAIL]", err);
            this.saveError = err.message;
            this.isSavingDraft = false;
            this.render();
          }
        }

        if (action === 'edit' && id) {
          const itemToEdit = id ? this.summary.upcoming.find(u => String(u.id) === String(id)) : null;
          if (itemToEdit && itemToEdit.content_id) {
            sessionStorage.setItem("library:openItemId", String(itemToEdit.content_id));
            sessionStorage.setItem("library:autoOpen", "1");
            this.activeMoreMenuId = null;
            window.location.hash = "#library";
            return;
          }

          // Fallback: Create draft if no content_id
          this.loadingIds.add(id);
          this.render();
          try {
            const res = await client.saveToLibrary({
              items: [{
                platform: itemToEdit.platform,
                content: itemToEdit.contentFull,
                status: "draft",
                metadata: { source: "home_edit_fallback", original_id: id }
              }]
            });
            if (res.success && res.ids && res.ids.length > 0) {
              const newId = res.ids[0];
              sessionStorage.setItem("library:openItemId", String(newId));
              sessionStorage.setItem("library:autoOpen", "1");
              this.activeMoreMenuId = null;
              window.location.hash = "#library";
            }
          } catch (err) {
            toast('error', "Draft creation failed: " + err.message);
          } finally {
            this.loadingIds.delete(id);
            this.render();
          }
          return;
        }

        if (action === 'cancel' && id) {
          this.editingIds.delete(id);
          this.render();
        }

        if (action === 'save' && id) {
          const textarea = homeRoot.querySelector(`textarea[data-id="${id}"]`);
          this.loadingIds.add(id);
          this.render();
          try {
            await client.updateScheduledPost(id, { content: textarea.value });
            this.editingIds.delete(id);
          } catch (err) {
            toast('error', "Save failed: " + err.message);
          } finally {
            this.loadingIds.delete(id);
            await this.refresh();
          }
        }

        if (action === 'shift' && id) {
          const shift = actionBtn.dataset.val;
          const d = new Date(item.scheduled_for);
          if (shift === '+1h') d.setHours(d.getHours() + 1);
          if (shift === '-1h') d.setHours(d.getHours() - 1);
          if (shift === '+1d') d.setDate(d.getDate() + 1);
          if (shift === '-1d') d.setDate(d.getDate() - 1);

          this.loadingIds.add(id);
          this.activeMoreMenuId = null;
          this.render();
          try {
            await client.updateScheduledPost(id, { scheduled_for: d.toISOString() });
          } finally {
            this.loadingIds.delete(id);
            await this.refresh();
          }
        }

        if (action === 'delete' && id) {
          if (await confirmModal({ title: 'Delete Post', message: 'Delete this scheduled post?', confirmLabel: 'Delete', danger: true })) {
            this.loadingIds.add(id);
            this.activeMoreMenuId = null;
            this.render();
            try {
              await client.deleteScheduledPost(id);
            } finally {
              this.loadingIds.delete(id);
              await this.refresh();
            }
          }
        }

        if (action === 'publish-now' && id) {
          const btn = e.target.closest('[data-action="publish-now"]');
          if (btn) { btn.disabled = true; btn.textContent = 'Publishing...'; }
          try {
            await client.publishNow(id);
            await this.refresh();
          } catch (err) {
            console.error('[HOME] publish-now failed:', err.message);
            if (btn) { btn.disabled = false; btn.textContent = 'Publish Now'; }
          }
          return;
        }

        if (action === 'nav-calendar') {
          window.location.hash = "#calendar";
        }
      }
    };
  }

  computeChecklist(summary) {
    const { brain, counts, onboarding } = summary;
    const mission = !brain.missingFields.includes('mission');
    const cta = !brain.missingFields.includes('cta');
    const facts = (brain.factsCount || 0) >= 3;
    const generate = (counts.drafts || 0) > 0;
    const schedule = (counts.scheduledTotal || 0) > 0;
    const aiKey = !!(onboarding && onboarding.hasAiKey);
    const socialConnected = !!(onboarding && onboarding.hasSocialConnected);

    return { mission, cta, facts, generate, schedule, aiKey, socialConnected };
  }

  _dateGreeting() {
    const now = new Date();
    const h = now.getHours();
    const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
    const day = now.toLocaleDateString('en-US', { weekday: 'long' });
    const date = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${day}, ${date} · ${greeting}`;
  }

  _dashboardSubtitle(counts) {
    if (!counts) return 'Your command center at a glance.';
    const active = counts.activeCampaigns || 0;
    if (active === 0) return 'Your command center at a glance.';
    return `${active} active campaign${active !== 1 ? 's' : ''}`;
  }

  async mount(container) {
    this.container = container;

    this.bindEvents();
    window.addEventListener("scroll", this._onScrollClose, true);

    // Refetch KPIs + upcoming content whenever content mutates elsewhere.
    this._onContentChanged = () => {
      clearTimeout(this._refetchTimer);
      this._refetchTimer = setTimeout(() => { this.refresh(); }, 350);
    };
    window.addEventListener('heist:content-changed', this._onContentChanged);

    await this.refresh();

    // The setup orchestrator (booted in main.js) owns wizard / tour
    // sequencing now — no longer kicked from this page. Leaves home.js
    // free to focus on the persistent Getting Started checklist below.
  }

  async unmount() {

    document.removeEventListener("keydown", this._handleEsc);
    document.removeEventListener("click", this._onDocClick, true);
    window.removeEventListener("scroll", this._onScrollClose, true);
    if (this._onContentChanged) {
      window.removeEventListener('heist:content-changed', this._onContentChanged);
      this._onContentChanged = null;
    }
    clearTimeout(this._refetchTimer);
    this._eventsBound = false;
  }

  _handleEsc = (e) => {
    if (e.key === "Escape") {
      if (this.selectedItemId || this.activeMoreMenuId) {
        this.selectedItemId = null;
        this.activeMoreMenuId = null;
        this.render();
      }
    }
  };

  async refresh() {
    try {
      const [summary, aiHealth, trendsRes] = await Promise.all([
        client.getDashboardSummary(),
        client.healthAi(),
        client.getDashboardTrends().catch(() => ({ days: [] }))
      ]);
      this.summary = summary;
      this.aiHealth = aiHealth;
      this.trends = (trendsRes && trendsRes.days) || [];
      this.checklist = this.computeChecklist(summary);
      this.render();
    } catch (err) {
      console.error("[HOME] refresh failed:", err);
      this.container.innerHTML = `
        <div style="padding: 24px; text-align: center;">
          <p>Failed to load dashboard: ${escapeHtml(err.message)}</p>
          <button onclick="window.location.reload()">Retry</button>
        </div>
      `;
    }
  }

  _sparkline(field) {
    var days = this.trends || [];
    if (days.length < 2) {
      return '<svg viewBox="0 0 200 40" class="kpi-sparkline"><line x1="10" y1="20" x2="190" y2="20" stroke="var(--text-muted)" stroke-width="1.5" stroke-dasharray="6 4" opacity="0.4"/></svg>';
    }
    var vals = [];
    for (var i = 0; i < days.length; i++) vals.push(days[i][field] || 0);
    var max = Math.max.apply(null, vals);
    if (max === 0) max = 1;
    var points = [];
    for (var j = 0; j < vals.length; j++) {
      var x = 10 + (j / (vals.length - 1)) * 180;
      var y = 36 - (vals[j] / max) * 32;
      points.push(Math.round(x) + ',' + Math.round(y * 10) / 10);
    }
    var first = vals[0]; var last = vals[vals.length - 1];
    var trend = last > first ? 'up' : last < first ? 'down' : 'flat';
    var arrow = trend === 'up' ? '<span class="kpi-arrow kpi-arrow-up">\u2191</span>' : trend === 'down' ? '<span class="kpi-arrow kpi-arrow-down">\u2193</span>' : '<span class="kpi-arrow kpi-arrow-flat">\u2192</span>';
    return '<svg viewBox="0 0 200 40" class="kpi-sparkline"><polyline fill="none" stroke="var(--primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" points="' + points.join(' ') + '"/></svg>' + arrow;
  }

  render() {
    if (!this.summary || !this.aiHealth) {
      this.container.innerHTML = `
        <div class="dashboard-page" style="padding: 24px;">
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-text"></div>
          <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin:24px 0;">
            <div class="skeleton skeleton-kpi"></div>
            <div class="skeleton skeleton-kpi"></div>
            <div class="skeleton skeleton-kpi"></div>
            <div class="skeleton skeleton-kpi"></div>
          </div>
          <div class="skeleton skeleton-card"></div>
          <div class="skeleton skeleton-card"></div>
        </div>
      `;
      return;
    }

    const { counts, upcoming, brain } = this.summary;
    const { mode, provider } = this.aiHealth;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Brand Intelligence dial shows the shared 7-field Brain-completeness
    // score (B1 roadmap #5) — same number as the sidebar badge and the
    // Brain page. The 8-item checklist below the dial stays a separate
    // to-do list ("N items need attention"), not a score.
    const brainScore = computeBrainScore(brain.profile);
    const mcpConnected = !!(window.__auth__ && window.__auth__.mcp_connected);
    const checklistCount = Object.values(this.checklist).filter(Boolean).length + (mcpConnected ? 1 : 0);
    const checklistTotal = 8;
    // Keep the Getting-Started bar visible until ALL 4 dynamic onboarding
    // items are green (mission, AI key, generate, MCP) — the user can
    // dismiss it explicitly with the X. The old "hide at 3+" threshold
    // pre-dates the MCP step and would hide the bar before MCP could be
    // checked off.
    const mcpDone = !!(window.__auth__ && window.__auth__.mcp_connected);
    const onboardingItemsDone = (this.checklist.mission ? 1 : 0)
        + (this.checklist.aiKey ? 1 : 0)
        + (this.checklist.generate ? 1 : 0)
        + (mcpDone ? 1 : 0);
    const showOnboarding = !this.onboardingDismissed && onboardingItemsDone < 4;

    this.container.innerHTML = `
      <div class="dashboard-page" id="page-home">
        ${renderPageHeader({
          eyebrow: this._dateGreeting(),
          title: "Command Center",
          subtitle: this._dashboardSubtitle(counts),
          tourPlaylist: 'home',
          rightStat: {
            label: "This week",
            value: `${counts.scheduledNext7d} post${counts.scheduledNext7d !== 1 ? 's' : ''} queued`
          }
        })}

        ${this.aiHealth.forceStub || this.aiHealth.effectiveKeySource === 'none' ? `
        <div class="page-shell" style="padding-bottom: 0;">
          <div class="stub-warning-banner" data-testid="stub-warning-banner" style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
            <div>
              <strong>Connect your AI to generate real content.</strong>
              Heist is showing placeholder posts — generate free from Claude or ChatGPT by connecting Heist to your AI chat, or <a href="#settings?section=section-ai-settings" style="color:inherit;text-decoration:underline;">add your own AI key</a>.
            </div>
            <a href="#connectors" class="btn btn-primary btn-sm" style="white-space:nowrap;flex-shrink:0;">Connect your AI chat &rarr;</a>
          </div>
        </div>
        ` : ''}

        ${!this.warningsDismissed && this.renderIntegrationWarnings() ? `
        <div class="page-shell" style="padding-bottom: 0;">
          ${this.renderIntegrationWarnings()}
        </div>
        ` : ''}

        ${showOnboarding ? (() => {
          var mcpDone = !!(window.__auth__ && window.__auth__.mcp_connected);
          var steps = [
            { done: true, label: 'Create account', href: null },
            { done: this.checklist.mission, label: 'Set up your Brain', href: '#memory?focus=mission' },
            { done: this.checklist.aiKey, label: 'Configure AI provider', href: '#settings?section=section-ai-settings' },
            { done: this.checklist.generate, label: 'Generate your first content', href: '#create?prefill=sample' },
            { done: this.checklist.socialConnected, label: 'Connect a social account', href: '#settings?section=section-integrations' },
            { done: mcpDone, label: 'Connect Claude or ChatGPT', href: '#connectors' }
          ];
          var doneCount = 0;
          for (var si = 0; si < steps.length; si++) { if (steps[si].done) doneCount++; }
          var pct = Math.round((doneCount / steps.length) * 100);
          var stepsHtml = '';
          for (var sj = 0; sj < steps.length; sj++) {
            var s = steps[sj];
            var cls = s.done ? 'gs-step gs-step--done' : (sj === doneCount ? 'gs-step gs-step--current' : 'gs-step');
            stepsHtml += s.href && !s.done
              ? '<a href="' + s.href + '" class="' + cls + '">' + (s.done ? '&#10003; ' : '') + s.label + '</a>'
              : '<div class="' + cls + '">' + (s.done ? '&#10003; ' : '') + s.label + '</div>';
          }
          return `
        <div class="page-shell" style="padding-bottom: 0;">
          <div class="getting-started-bar" data-testid="onboarding-banner">
            <div class="getting-started-header">
              <span class="getting-started-title">Getting started with Heist</span>
              <span class="getting-started-progress">${doneCount} of ${steps.length} complete</span>
              <button data-action="dismiss-onboarding" class="getting-started-dismiss" title="Dismiss">\u2715</button>
            </div>
            <div class="getting-started-track"><div class="getting-started-fill" style="width:${pct}%"></div></div>
            <div class="getting-started-steps">${stepsHtml}</div>
          </div>
        </div>`;
        })() : ''}

        <div class="page-shell page-content">
          ${(counts.scheduledNext7d === 0 && counts.drafts === 0 && counts.activeCampaigns === 0 && counts.failures === 0 && !showOnboarding) ? `
          <!-- Empty-state hero \u2014 replaces the all-zero KPI grid for new
               accounts so the dashboard reads "welcome" instead of
               "you've already failed every metric." Once any KPI is
               non-zero the regular grid takes over. Suppressed while the
               Getting-Started checklist is visible (B1 F-UX-2) so a new
               account sees ONE primary onboarding surface, not two. -->
          <div class="dashboard-welcome" data-testid="dashboard-welcome-hero">
            <div class="dashboard-welcome__halo" aria-hidden="true"></div>
            <div class="dashboard-welcome__inner">
              <div class="dashboard-welcome__eyebrow">Welcome to Heist</div>
              <h2 class="dashboard-welcome__title">This is your command center.</h2>
              <p class="dashboard-welcome__lede">It looks empty because you haven't created anything yet \u2014 that's expected. Pick how you want to start, and your scheduled posts, campaigns, and metrics will fill in here.</p>
              <div class="dashboard-welcome__actions">
                <a href="#connectors" class="btn btn-primary dashboard-welcome__cta dashboard-welcome__cta--primary" data-testid="dashboard-welcome-connect">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  <span>Connect your AI chat</span>
                </a>
                <a href="#memory?tab=chat&new=1" class="btn btn-glass dashboard-welcome__cta" data-testid="dashboard-welcome-chat">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <span>Try Heist's built-in chat</span>
                </a>
                <a href="#create" class="dashboard-welcome__quiet" data-testid="dashboard-welcome-studio">Or write a post in Studio \u2192</a>
              </div>
            </div>
          </div>
          ` : `
          <!-- KPI Cards -->
          <div class="kpi-grid">
            <div class="kpi kpi-blue" data-testid="dashboard-kpi-scheduled" data-action="nav-kpi" data-target="calendar">
              <div class="kpi-meta"><div class="kpi-lbl">Scheduled (7d)</div><div class="kpi-ic kpi-ic-b"><svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div></div>
              <div class="kpi-val">${counts.scheduledNext7d}</div>
              <div class="kpi-trend kpi-neu">${counts.scheduledNext7d > 0 ? 'Next 7 days' : 'None scheduled'}</div>
              <div class="kpi-spark">${this._sparkline('scheduled')}</div>
            </div>
            <div class="kpi kpi-green" data-testid="dashboard-kpi-drafts" data-action="nav-kpi" data-target="library">
              <div class="kpi-meta"><div class="kpi-lbl">Published</div><div class="kpi-ic kpi-ic-g"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></div></div>
              <div class="kpi-val">${counts.drafts}</div>
              <div class="kpi-trend kpi-neu">${counts.drafts > 0 ? counts.drafts + ' total' : 'No drafts yet'}</div>
              <div class="kpi-spark">${this._sparkline('published')}</div>
            </div>
            <div class="kpi kpi-violet" data-testid="dashboard-kpi-campaigns" data-action="nav-kpi" data-target="campaigns">
              <div class="kpi-meta"><div class="kpi-lbl">Campaigns</div><div class="kpi-ic kpi-ic-v"><svg viewBox="0 0 24 24"><path d="m3 11 18-5v12L3 14v-3z"/></svg></div></div>
              <div class="kpi-val">${counts.activeCampaigns}</div>
              <div class="kpi-trend kpi-neu">${counts.activeCampaigns} active</div>
              <div class="kpi-spark">${this._sparkline('created')}</div>
            </div>
            <div class="kpi ${counts.failures > 0 ? 'kpi-red' : 'kpi-clear'}" data-testid="dashboard-kpi-failures" data-action="nav-kpi" data-target="calendar">
              <div class="kpi-meta"><div class="kpi-lbl">Failures</div><div class="kpi-ic ${counts.failures > 0 ? 'kpi-ic-r' : 'kpi-ic-g'}"><svg viewBox="0 0 24 24">${counts.failures > 0 ? '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>' : '<polyline points="20 6 9 17 4 12"/>'}</svg></div></div>
              <div class="kpi-val">${counts.failures}</div>
              <div class="kpi-trend ${counts.failures > 0 ? 'kpi-down' : 'kpi-up'}">${counts.failures > 0 ? '\u25BC requires attention' : '\u2713 All clear'}</div>
              <div class="kpi-spark">${this._sparkline('failures')}</div>
            </div>
          </div>
          `}

          <!-- Two-column layout -->
          <div class="home-layout">
            <!-- Left: Upcoming Content -->
            <div>
              <div class="dashboard-card" data-testid="dashboard-upcoming">
                <div class="card-title" style="display:flex;justify-content:space-between;align-items:center;">
                  <div>
                    <div style="font-size:13px;font-weight:700;letter-spacing:-0.01em;color:var(--text);text-transform:none;">Upcoming Content</div>
                    <div style="font-size:11px;color:var(--text-muted);margin-top:2px;text-transform:none;letter-spacing:normal;font-weight:400;">${tz} timezone</div>
                  </div>
                  <a href="#calendar" style="font-size:12px;font-weight:600;color:var(--primary-text);text-decoration:none;text-transform:none;letter-spacing:normal;">View Calendar \u2192</a>
                </div>
                <div class="schedule-header-row">
                  <span>Time</span>
                  <span>Platform</span>
                  <span>Preview</span>
                  <span>Campaign</span>
                  <span>Status</span>
                  <span></span>
                </div>
                ${this.renderUpcomingRows(upcoming)}
              </div>
            </div>

            <!-- Right: Sidebar -->
            <div class="home-sidebar">
              <div class="dashboard-card bi-card" data-testid="dashboard-checklist">
                <div class="bi-hd">
                  <div>
                    <div class="bi-title">Brand Intelligence</div>
                    <div class="bi-sub">Brain completeness</div>
                  </div>
                  <a href="#memory" class="bi-action">Edit \u2192</a>
                </div>
                <div class="bi-body">
                  <div class="bi-dial-row">
                    <div class="bi-dial">
                      <svg width="72" height="72" viewBox="0 0 72 72">
                        <defs><linearGradient id="bi-grad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#6366F1"/><stop offset="100%" stop-color="#A78BFA"/></linearGradient></defs>
                        <circle class="bi-dial-track" cx="36" cy="36" r="30"/>
                        <circle class="bi-dial-fill" cx="36" cy="36" r="30" style="stroke-dashoffset:${Math.round(188 - (188 * brainScore.pct / 100))}"/>
                      </svg>
                      <div class="bi-dial-center">${brainScore.pct}%</div>
                    </div>
                    <div>
                      <div class="bi-info-lbl">Status</div>
                      <div class="bi-info-val">${brainScore.pct >= 80 ? 'Great Shape' : brainScore.pct >= 40 ? 'Good Shape' : 'Getting Started'}</div>
                      <div class="bi-info-sub">${checklistTotal - checklistCount} item${checklistTotal - checklistCount !== 1 ? 's' : ''} need${checklistTotal - checklistCount === 1 ? 's' : ''} attention</div>
                    </div>
                  </div>
                  <div class="bi-checklist">
                    ${this.renderChecklistItem("Connected to your AI chat (MCP)", mcpConnected, "#connectors")}
                    ${this.renderChecklistItem("AI API key configured", this.checklist.aiKey, "#settings")}
                    ${this.renderChecklistItem("Social account connected", this.checklist.socialConnected, "#settings")}
                    ${this.renderChecklistItem("Brand mission set", this.checklist.mission, "#memory")}
                    ${this.renderChecklistItem("CTA configured", this.checklist.cta, "#memory")}
                    ${this.renderChecklistItem("Memory facts (" + (this.summary.brain.factsCount || 0) + " added)", this.checklist.facts, "#memory")}
                    ${this.renderChecklistItem("First batch generated", this.checklist.generate, "#create")}
                    ${this.renderChecklistItem("Post scheduled", this.checklist.schedule, "#calendar")}
                  </div>
                </div>
              </div>

              <div class="gmw-home-card" id="gmw-home-launch">
                <div class="gmw-home-card-text">
                  <h3><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary,#8B5CF6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:4px;"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>Plan a campaign</h3>
                  <p>One idea → 14–30 scheduled posts</p>
                </div>
                <button class="gmw-home-card-btn">Get Started →</button>
              </div>

              <div class="dashboard-card qa-card" data-testid="dashboard-quick-actions">
                <div class="bi-hd"><div class="bi-title">Quick Actions</div></div>
                <div class="qa-grid">
                  <a href="#create" class="qa quick-action-item"><div class="qa-ic qa-i"><svg viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></div><span class="qa-lbl">Content Studio</span></a>
                  <a href="#campaigns" class="qa quick-action-item"><div class="qa-ic qa-v"><svg viewBox="0 0 24 24"><path d="m3 11 18-5v12L3 14v-3z"/></svg></div><span class="qa-lbl">Campaigns</span></a>
                  <a href="#calendar" class="qa quick-action-item"><div class="qa-ic qa-b"><svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div><span class="qa-lbl">Calendar</span></a>
                  <a href="#memory" class="qa quick-action-item"><div class="qa-ic qa-e"><svg viewBox="0 0 24 24"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-2.04z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-2.04z"/></svg></div><span class="qa-lbl">Brand Intelligence</span></a>
                </div>
              </div>

              <!-- Strategy Engine widget removed in 11B -->
            </div>
          </div>
        </div>

        <!-- Details Drawer -->
        ${this.renderDetailsDrawer()}

      </div>
    `;

    this.bindEvents();
  }

  renderIntegrationWarnings() {
    const warnings = this.summary?.integrationWarnings || [];
    if (warnings.length === 0) return '';
    const items = warnings.map(w => {
      const msg = w.issue === 'not_connected'
        ? `<strong>${w.platform}</strong> has scheduled posts but no connected integration`
        : `<strong>${w.platform}</strong> integration is in error state — posts may fail`;
      return `<li style="margin:4px 0;">${msg}</li>`;
    }).join('');
    return `<div style="background:var(--warning-bg);border:1px solid var(--warning);border-radius:8px;padding:12px 16px;margin-bottom:16px;position:relative;" data-testid="integration-warnings">
      <button data-action="dismiss-warnings" style="position:absolute;top:8px;right:12px;background:none;border:none;font-size:18px;color:var(--text);cursor:pointer;padding:0 4px;" title="Dismiss" aria-label="Dismiss publishing warnings">&times;</button>
      <strong style="color:var(--text);">Publishing Issues Detected</strong>
      <ul style="margin:8px 0 4px 16px;color:var(--text-2);font-size:13px;">${items}</ul>
      <a href="#settings" style="font-size:13px;color:var(--warning);font-weight:600;">Fix in Settings →</a>
    </div>`;
  }

  renderChecklistItem(label, isDone, link) {
    const icon = isDone
      ? `<div class="chk-ic chk-done"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg></div>`
      : `<div class="chk-ic chk-todo"><svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></div>`;
    return `
      <div class="chk-item checklist-item">
        ${icon}
        <span class="chk-lbl">${label}</span>
        ${!isDone ? `<a href="${link}" class="chk-fix">Fix</a>` : ''}
      </div>
    `;
  }

  renderUpcomingRows(upcoming) {
    if (upcoming.length === 0) {
      return '<div style="text-align: center; color: var(--text-muted); padding: 40px; border: 1px dashed var(--border); border-radius: 8px;">No tasks in queue.</div>';
    }

    const groups = {};
    upcoming.forEach(item => {
      const d = new Date(item.scheduled_for);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      let key = d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
      if (d.toDateString() === today.toDateString()) key = "Today";
      else if (d.toDateString() === tomorrow.toDateString()) key = "Tomorrow";

      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    return Object.keys(groups).map(key => `
      <div class="schedule-group">
        <div class="schedule-group-title">${key}</div>
        ${groups[key].map(item => this.renderTableRow(item)).join('')}
      </div>
    `).join('');
  }

  renderTableRow(item) {
    const isSaving = this.loadingIds.has(item.id);
    const timeStr = new Date(item.scheduled_for).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const isSelected = this.selectedItemId === item.id;

    const platMap = {
      facebook:  { cls: 'pp-fb',   label: 'Facebook'  },
      instagram: { cls: 'pp-ig',   label: 'Instagram'  },
      x:         { cls: 'pp-x',    label: 'X'          },
      twitter:   { cls: 'pp-x',    label: 'X'          },
      linkedin:  { cls: 'pp-li',   label: 'LinkedIn'   },
      blog:      { cls: 'pp-blog', label: 'WordPress'  },
      website:   { cls: 'pp-website', label: 'Website' },
    };
    const p = platMap[item.platform] || { cls: 'pp-fb', label: item.platform };
    const platBadge = `<span class="pp ${p.cls}"><span class="pp-dot"></span>${p.label}</span>`;

    const statusMap = {
      scheduled:   { cls: 'sb-b sb-sched', dot: true,  label: 'Scheduled' },
      published:   { cls: 'sb-b sb-pub',   dot: true,  label: 'Published' },
      failed:      { cls: 'sb-b sb-fail',  dot: false, label: 'Failed'    },
      dead_letter: { cls: 'sb-b sb-fail',  dot: false, label: 'Failed'    },
      draft:       { cls: 'sb-b sb-draft', dot: false, label: 'Draft'     },
    };
    const s = statusMap[(item.status || 'draft').toLowerCase()]
           || { cls: 'sb-b sb-draft', dot: false, label: 'Draft' };
    const statusBadge = `<span class="${s.cls}">${s.dot ? '<span class="sb-dot"></span>' : ''}${s.label}</span>`;

    return `
      <div class="schedule-row ${isSelected ? 'active' : ''}" data-id="${item.id}">
        <div class="col-time">${timeStr}</div>
        <div class="col-platform">
          ${platBadge}
        </div>
        <div class="col-content" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;">${escapeHtml(item.contentSnippet || 'No preview')}</div>
        <div class="col-campaign">${escapeHtml(item.campaignName || '—')}</div>
        <div class="col-status">
          ${statusBadge}
        </div>
        <div class="actions-cell">
          ${isSaving ? '<span class="saving-indicator">Saving...</span>' : `
            <button class="btn-icon-sm" data-action="open-more" data-id="${item.id}" title="More" aria-label="More actions">⋯</button>
          `}
          ${this.renderMoreMenu(item.id)}
        </div>
      </div>
    `;
  }

  renderMoreMenu(id) {
    const isOpen = this.activeMoreMenuId === id;
    const item = this.summary.upcoming.find(u => String(u.id) === String(id));
    if (!item) return '';

    return `
      <div class="more-menu ${isOpen ? 'open' : ''}" data-id="${id}">
        <button class="menu-item" data-action="shift" data-id="${id}" data-val="-1h"><span class="menu-icon">${_homeIcon('rewind')}</span> Move -1h</button>
        <button class="menu-item" data-action="shift" data-id="${id}" data-val="+1h"><span class="menu-icon">${_homeIcon('fast-forward')}</span> Move +1h</button>
        <button class="menu-item" data-action="shift" data-id="${id}" data-val="-1d"><span class="menu-icon">${_homeIcon('skip-back')}</span> Move -1d</button>
        <button class="menu-item" data-action="shift" data-id="${id}" data-val="+1d"><span class="menu-icon">${_homeIcon('skip-forward')}</span> Move +1d</button>
        <div class="menu-divider"></div>
        <button class="menu-item" data-action="nav-calendar" data-date="${item.scheduled_for}"><span class="menu-icon">${_homeIcon('calendar')}</span> Open in Calendar</button>
        <button class="menu-item" data-action="publish-now" data-id="${id}"><span class="menu-icon">${_homeIcon('send')}</span> Publish Now</button>
        <div class="menu-divider"></div>
        <button class="menu-item" style="color: #ef4444" data-action="delete" data-id="${id}"><span class="menu-icon">${_homeIcon('trash')}</span> Delete</button>
      </div>
    `;
  }

  renderDetailsDrawer() {
    if (!this.selectedItemId) return '<div class="details-drawer"></div>';
    const item = this.summary.upcoming.find(u => String(u.id) === String(this.selectedItemId));
    if (!item) return '<div class="details-drawer"></div>';

    const isEditing = this.editingIds.has(item.id);
    const isDuplicate = this.isDuplicateMode;
    const isSaving = this.isSavingDraft;

    return `
      <div class="drawer-backdrop" style="position:fixed; inset:0; background:rgba(0,0,0,0.2); z-index:999;" data-action="close-drawer"></div>
      <aside class="details-drawer open">
        <div class="details-header">
          <div>
            <h3 style="margin:0">${isDuplicate ? 'Duplicate Draft' : (item.platform.toUpperCase() + ' Post')}</h3>
            <div style="font-size: 12px; color: var(--text-muted); margin-top: 4px;">
              ${isDuplicate ? 'Create a copy in your library' : new Date(item.scheduled_for).toLocaleString()}
            </div>
          </div>
          <button class="btn-icon-sm" data-action="close-drawer" aria-label="Close"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        </div>
        
        <div class="details-body">
           ${this.saveError ? `<div style="background:var(--danger-bg); color:var(--danger); padding:10px; border-radius:6px; margin-bottom:16px; font-size:13px;">${escapeHtml(this.saveError)}</div>` : ''}
           
           <div style="margin-bottom: 24px;">
              <div class="card-title" style="font-size: 11px; margin-bottom: 8px;">Campaign</div>
              <div style="font-weight: 600;">${escapeHtml(item.campaignName || 'No Campaign')}</div>
           </div>

           <div style="margin-bottom: 24px;">
              <div class="card-title" style="font-size: 11px; margin-bottom: 8px;">Content</div>
              ${isEditing || isDuplicate ? `
                <div class="editor-container" style="background: var(--surface-2); padding: 12px; border-radius: 8px;">
                  <textarea style="width:100%; min-height: 200px; border:1px solid var(--border); border-radius: 4px; padding: 12px; font-size: 14px;" data-id="${item.id}">${item.contentFull || ''}</textarea>
                  <div style="display:flex; justify-content: flex-end; gap: 8px; margin-top: 12px;">
                    ${isDuplicate ? `
                      <button class="btn btn-secondary btn-sm" data-action="cancel-duplicate">Cancel</button>
                      <button class="btn btn-glass btn-sm" data-action="create-draft" data-id="${item.id}" ${isSaving ? 'disabled' : ''}>
                        ${isSaving ? 'Creating...' : 'Create Draft'}
                      </button>
                    ` : `
                      <button class="btn btn-secondary btn-sm" data-action="cancel" data-id="${item.id}">Cancel</button>
                      <button class="btn btn-glass btn-sm" data-action="save" data-id="${item.id}">Save Changes</button>
                    `}
                  </div>
                </div>
              ` : `
                <div style="background: var(--surface-2); padding: 16px; border-radius: 8px; font-size: 14px; line-height: 1.6; white-space: pre-wrap; border: 1px solid var(--border);">${escapeHtml(item.contentFull || '')}</div>
              `}
           </div>
        </div>

        <div class="details-actions">
           ${!(isEditing || isDuplicate) ? `
             <button class="btn btn-secondary btn-sm" data-action="duplicate" data-id="${item.id}">Duplicate</button>
             <button class="btn btn-secondary btn-sm" data-action="edit" data-id="${item.id}">Edit in Library</button>
             <button class="btn btn-glass btn-sm" data-action="nav-calendar" data-date="${item.scheduled_for}" style="margin-left:auto">Open in Calendar</button>
             <button class="btn btn-primary btn-sm" data-action="publish-now" data-id="${item.id}" style="background:var(--success); border-color:var(--success);">Publish Now</button>
           ` : ''}
        </div>
      </aside>
    `;
  }

  bindEvents() {
    if (this._eventsBound) return;
    document.addEventListener("keydown", this._handleEsc);
    document.addEventListener("click", this._onDocClick, true);
    this._eventsBound = true;
  }
}
